import type { MiceStatusCounts } from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { Button, Dot, StackedBar } from '@web/ui/core';

interface ExecutionStats {
  issued: number;
  /** Emitindo (aguardando emissão) ou esperando a aprovação corporativa. */
  awaitingIssuance: number;
  buying: number;
  queued: number;
  /** Pedidos que falharam ou ficaram indeterminados. Não é o "precisam de você": esse conta
   *  viajantes da fase Atenção (`phaseCounts.attention`), a mesma conta da lista que ele abre. */
  problem: number;
  /** Pedidos que contam na compra: inválidos e duplicados nunca viram bilhete. */
  total: number;
}

/** Os números da compra, para a linha de status e para o painel Resumo. */
export function executionStats(c: MiceStatusCounts): ExecutionStats {
  const total = Object.entries(c)
    .filter(([status]) => status !== 'invalid' && status !== 'duplicate')
    .reduce((sum, [, n]) => sum + n, 0);
  return {
    issued: c.issued,
    awaitingIssuance: c.awaiting_issuance + c.awaiting_approval,
    buying: c.purchasing,
    queued: c.queued,
    problem: c.failed + c.indeterminate,
    total,
  };
}

interface ExecutionSummaryProps {
  counts: MiceStatusCounts;
  /** Viajantes na fase Atenção (`phaseCounts.attention`). */
  needsYou: number;
  /** Leva a lista para a fase Atenção. */
  onShowAttention: () => void;
}

/** Progresso da compra no painel Resumo: emitidos, emitindo, comprando, na fila e problemas. */
export function ExecutionSummary({
  counts,
  needsYou,
  onShowAttention,
}: Readonly<ExecutionSummaryProps>) {
  const s = executionStats(counts);
  if (s.total === 0) return null;
  const legend = [
    { key: 'issued', tone: 'green', value: s.issued },
    { key: 'awaiting', tone: 'green', value: s.awaitingIssuance },
    { key: 'purchasing', tone: 'ai', value: s.buying },
    { key: 'queued', tone: 'gray', value: s.queued },
    { key: 'problem', tone: 'red', value: s.problem },
  ] as const;
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <span className="font-serif text-lg font-semibold text-foreground">
        {t('mice.exec.title', { issued: s.issued, total: s.total })}
      </span>
      <StackedBar
        label={t('mice.exec.bar', {
          issued: s.issued,
          awaiting: s.awaitingIssuance,
          purchasing: s.buying,
          queued: s.queued,
          problem: s.problem,
        })}
        segments={[
          { key: 'issued', value: s.issued, tone: 'green' },
          { key: 'awaiting', value: s.awaitingIssuance, tone: 'green-soft' },
          { key: 'purchasing', value: s.buying, tone: 'ai' },
          { key: 'queued', value: s.queued, tone: 'ai-soft' },
          { key: 'problem', value: s.problem, tone: 'red' },
        ]}
      />
      <div className="grid grid-cols-2 gap-3">
        {legend.map((item) => (
          <div key={item.key} className="flex flex-col gap-0.5">
            <span className="flex items-center gap-1.5 text-micro text-muted-foreground">
              <Dot tone={item.tone} />
              {t(`mice.exec.${item.key}`)}
            </span>
            <span className="font-serif text-lg font-semibold text-foreground">{item.value}</span>
          </div>
        ))}
      </div>
      {needsYou > 0 ? (
        <div>
          <Button size="sm" variant="outline" onClick={onShowAttention}>
            {t('mice.exec.attentionLink', { count: needsYou })}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
